import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  ReleaseDetail,
  ReleasePage,
  ReleaseRow,
  ReleaseStatusEffective,
} from '@/generated/types/navigator/types.gen';
import { baseQueryWithAuth } from './utils';

export type ReleaseSortField =
  | 'status'
  | 'created_at'
  | 'updated_at'
  | 'scan_label';
export type ReleaseSortOrder = 'asc' | 'desc';

export interface DraftReleaseRun {
  run_id: string;
  status: string;
  client_code: string | null;
  internal_name: string | null;
  created_by_email: string | null;
  started_at: number | null;
  url: string;
}

interface ScanOption {
  scan_id: string;
  label: string | null;
}

export interface DemoResetResult {
  client_code: string;
  fulcrum_records_deleted: number;
  fulcrum_projects_deleted: number;
  work_logs_deleted: number;
  plans_deleted: number;
  errors: string[];
}

export type ScopeKind = 'delivery' | 'scan_wide';

interface ScopeOption {
  value: string;
  label: string;
  kind: ScopeKind;
}

export interface ScopeBatch {
  batch_id: string;
  scope: string;
  kind: ScopeKind;
  layer_type: string;
  created_at: string;
  is_published: boolean;
}

export interface PublishReleaseArgs {
  releaseId: string;
  displayName?: string;
  releaseIdToReplace?: string;
}

interface SearchReleasesArgs {
  clientCode: string;
  statuses?: ReleaseStatusEffective[];
  scanId?: string;
  deliveryIdentifier?: string;
  createdAfter?: string;
  createdBefore?: string;
  sortBy?: ReleaseSortField;
  sortOrder?: ReleaseSortOrder;
  page?: number;
  limit?: number;
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['AdminReleases', 'DraftRuns'],
  endpoints: (builder) => ({
    listReleases: builder.query<
      ReleaseRow[],
      { clientCode: string; statuses?: ReleaseStatusEffective[] }
    >({
      query: ({ clientCode, statuses }) => ({
        url: '/admin/releases/list',
        method: 'GET',
        params: {
          client_code: clientCode,
          ...(statuses ? { status: statuses } : {}),
        },
      }),
      providesTags: ['AdminReleases'],
    }),
    searchReleases: builder.query<ReleasePage, SearchReleasesArgs>({
      query: ({
        clientCode,
        statuses,
        scanId,
        deliveryIdentifier,
        createdAfter,
        createdBefore,
        sortBy,
        sortOrder,
        page,
        limit,
      }) => ({
        url: '/admin/releases/search',
        method: 'GET',
        params: {
          client_code: clientCode,
          ...(statuses ? { status: statuses } : {}),
          ...(scanId ? { scan_id: scanId } : {}),
          ...(deliveryIdentifier
            ? { delivery_identifier: deliveryIdentifier }
            : {}),
          ...(createdAfter ? { created_after: createdAfter } : {}),
          ...(createdBefore ? { created_before: createdBefore } : {}),
          ...(sortBy ? { sort_by: sortBy } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
          ...(page !== undefined ? { page } : {}),
          ...(limit !== undefined ? { limit } : {}),
        },
      }),
      providesTags: ['AdminReleases'],
    }),
    getReleaseDetail: builder.query<ReleaseDetail, { releaseId: string }>({
      query: ({ releaseId }) => ({
        url: `/admin/releases/${releaseId}`,
        method: 'GET',
      }),
      providesTags: ['AdminReleases'],
    }),
    revokeRelease: builder.mutation<{ id: string }, { releaseId: string }>({
      query: ({ releaseId }) => ({
        url: `/admin/releases/${releaseId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminReleases'],
    }),
    publishRelease: builder.mutation<
      { id: string; display_name: string | null },
      PublishReleaseArgs
    >({
      query: ({ releaseId, displayName, releaseIdToReplace }) => ({
        url: `/admin/releases/${releaseId}/publish`,
        method: 'POST',
        body: {
          ...(displayName !== undefined ? { display_name: displayName } : {}),
          ...(releaseIdToReplace !== undefined
            ? { release_id_to_replace: releaseIdToReplace }
            : {}),
        },
      }),
      invalidatesTags: ['AdminReleases'],
    }),
    bulkRevokeReleases: builder.mutation<
      { revoked: string[]; already_revoked: string[] },
      { releaseIds: string[] }
    >({
      query: ({ releaseIds }) => ({
        url: '/admin/releases/bulk-revoke',
        method: 'POST',
        body: { release_ids: releaseIds },
      }),
      invalidatesTags: ['AdminReleases'],
    }),
    renameRelease: builder.mutation<
      { id: string; display_name: string | null },
      { releaseId: string; displayName: string | null }
    >({
      query: ({ releaseId, displayName }) => ({
        url: `/admin/releases/${releaseId}`,
        method: 'PATCH',
        body: { display_name: displayName },
      }),
      invalidatesTags: ['AdminReleases'],
    }),
    draftRelease: builder.mutation<
      DraftReleaseRun,
      {
        clientCode: string;
        scanId: string;
        batchIds: string[];
        internalName?: string;
      }
    >({
      query: ({ clientCode, scanId, batchIds, internalName }) => ({
        url: '/admin/releases/draft',
        method: 'POST',
        body: {
          client_code: clientCode,
          scan_id: scanId,
          batch_ids: batchIds,
          internal_name: internalName,
        },
      }),
      invalidatesTags: ['DraftRuns', 'AdminReleases'],
    }),
    listDraftRuns: builder.query<
      DraftReleaseRun[],
      { clientCode: string; limit?: number }
    >({
      query: ({ clientCode, limit = 25 }) => ({
        url: '/admin/releases/draft/runs',
        method: 'GET',
        params: { client_code: clientCode, limit },
      }),
      providesTags: ['DraftRuns'],
    }),
    terminateDraftRun: builder.mutation<
      void,
      { runId: string; clientCode: string }
    >({
      query: ({ runId, clientCode }) => ({
        url: `/admin/releases/draft/runs/${runId}/terminate`,
        method: 'POST',
        body: { client_code: clientCode },
      }),
      invalidatesTags: ['DraftRuns'],
    }),
    listScans: builder.query<ScanOption[], { clientCode: string }>({
      query: ({ clientCode }) => ({
        url: '/admin/releases/scans',
        method: 'GET',
        params: { client_code: clientCode },
      }),
    }),
    listScopes: builder.query<
      ScopeOption[],
      { clientCode: string; scanId: string }
    >({
      query: ({ clientCode, scanId }) => ({
        url: '/admin/releases/scopes',
        method: 'GET',
        params: { client_code: clientCode, scan_id: scanId },
      }),
    }),
    listScopeBatches: builder.query<
      ScopeBatch[],
      {
        clientCode: string;
        scanId: string;
        scopes: string[];
        includePublished: boolean;
      }
    >({
      query: ({ clientCode, scanId, scopes, includePublished }) => ({
        url: '/admin/releases/batches',
        method: 'GET',
        params: {
          client_code: clientCode,
          scan_id: scanId,
          scope: scopes,
          include_published: includePublished,
        },
      }),
    }),
    resetDemoClient: builder.mutation<DemoResetResult, { clientCode: string }>({
      query: ({ clientCode }) => ({
        url: '/admin/demo-reset',
        method: 'POST',
        body: { client_code: clientCode },
      }),
    }),
  }),
});

export const {
  useListReleasesQuery,
  useSearchReleasesQuery,
  useGetReleaseDetailQuery,
  useRevokeReleaseMutation,
  usePublishReleaseMutation,
  useBulkRevokeReleasesMutation,
  useRenameReleaseMutation,
  useDraftReleaseMutation,
  useListDraftRunsQuery,
  useTerminateDraftRunMutation,
  useListScansQuery,
  useListScopesQuery,
  useListScopeBatchesQuery,
  useResetDemoClientMutation,
} = adminApi;

export const resetAdminApiState = adminApi.util.resetApiState;
